/**
 * ObjectIcon — Push #12+ standardized tile icon for every "object/area" the
 * app surfaces. Salesforce-style visual anchor: same icon + color travels
 * with the object from sidebar → page header → record references.
 *
 * The two-color split mirrors what AdminHome's workspace map ships:
 *   - teal = "things you DO" (Models, Connectors, Prompts — D-167 order;
 *            Home, Profile, Catalog, Chat, Capabilities)
 *   - obsidian = "things you GOVERN" (People, Permission sets, Activity,
 *                Inbox, Billing, Settings)
 *
 * Pick a `kind` from the OBJECT_KIND union. To add a new object: extend
 * the union + the registry below. Don't reach for the underlying tile-icon
 * div directly — going through ObjectIcon keeps the vocabulary single-
 * sourced so we can't accidentally pick a different glyph for the same
 * object on two different pages.
 *
 * Sizes:
 *   - xs: 20px (AdminSubNav items, TopNav user-mode items — dense nav)
 *   - sm: 28px (toolkit cards, list-row leading icons)
 *   - md: 40px (tile headers)
 *   - lg: 56px (lightning-highlight page headers, EmptyState illustrations)
 *
 * Variants:
 *   - solid (default): colored tile + white glyph. Page-header anchor.
 *   - soft: tinted tile (10% opacity) + colored glyph. EmptyState
 *     illustration — quieter visual weight for "nothing here yet" states
 *     without competing with the actual empty-state copy.
 */

import type { ReactElement } from 'react';

import {
  IconPlug,
  IconWave,
  IconBolt,
  IconPeople,
  IconLock,
  IconSparkle,
  IconInbox,
  IconReceipt,
  IconGear,
  IconBriefcase,
  IconUserCircle,
  IconGrid,
  IconChat,
  IconHome,
  IconLayers,
  IconAlert,
  IconShield,
  IconLink,
  IconUserCheck,
  IconBell,
  IconFolder,
  IconLibrary,
  IconMessage,
  IconClipboard,
  IconPackage,
  IconMail,
  IconPaperclip,
  IconCoin,
  IconEye,
  IconCookie,
  IconFileText,
  IconBarChart,
  IconDashboard,
  IconPulse,
} from './Icon.tsx';

export type ObjectKind =
  // Platform pillars — teal. D-167: Models → Connectors → Prompts
  | 'models'
  | 'connectors'
  | 'prompts'
  // User-mode surfaces — teal
  | 'home'
  | 'profile'
  | 'catalog'
  | 'chat'
  // Admin parent grouper — teal (Capabilities ▾ in AdminSubNav)
  | 'capabilities'
  // Admin / governance surfaces — obsidian
  | 'people'
  | 'permission-sets'
  | 'activity'
  | 'incidents'
  | 'inbox'
  | 'billing'
  | 'settings'
  // Push 5-post-smoke-32 (Sub-agent U, 2026-07-15) — Workspace admin
  // sidebar surface. Was 'settings' (gear); Ean twice called the gear
  // ugly and pointed out the Workspace concept is broader than
  // "preferences" (holds usage + billing + notifications tabs too).
  // Distinct kind so we can render a briefcase in the sidebar without
  // altering every chat-side "settings" glyph.
  | 'workspace'
  // Phase 2 Block 2B (2026-06-30) — record-page object marks. Per
  // execution plan § Block 2C icon mapping: every detail surface that
  // doesn't already have a pillar icon gets one of these. Operator-only
  // tables (Tier 5) → 'operator' shield. Junction audit pages (Tier 4)
  // → 'junction' chain link.
  | 'operator'
  | 'junction'
  // Phase 2 Block 2B per-table marks (Block 2C icon-lock list):
  | 'notification'         // sys__notification — bell, obsidian
  | 'invitation'           // sys__user_invitation — mail, obsidian
  | 'folder'               // chat__folder — folder, teal
  | 'message'              // chat__message — speech bubble, teal
  | 'attachment'           // chat__message_attachment — paperclip, teal
  | 'token-grant'          // billing__token_grant — receipt, obsidian
  | 'token-debit'          // billing__token_debit — coin, obsidian
  | 'approval-request'     // billing__approval_request — clipboard list, obsidian
  | 'product'              // billing__product — package, obsidian
  // Phase 4 pre-plumbing (2026-06-30) — documents capability marks.
  // Placeholder glyphs (IconLayers for document body, IconGrid for chunk
  // grid). Follow-up polish can swap to FileText + Rows3 from lucide once
  // those glyphs land in Icon.tsx.
  | 'document'             // knowledge__document — teal
  | 'document-chunk'       // knowledge__document_chunk — teal
  | 'library'              // Library pillar / collection surface (Push 4-1c-d)
  // Push 4-1o (2026-07-09) — legal/policy tab marks. Both on obsidian per
  // legal-page palette (Ean nitpick: "Make the cookies icon black/navy to
  // blend in"). Distinct glyphs so users don't confuse Privacy with
  // Permission sets (Lock) — Privacy uses IconEye (data-visibility framing)
  // and Cookies uses IconCookie (round disk with chips, distinct from Coin).
  | 'privacy'
  | 'cookies'
  // Push 64 (2026-07-16) — Terms of Service tab needs its own glyph
  // distinct from the parent Legal & policies hero (IconClipboard).
  // File-text (folded-corner document with text lines) reads as
  // "written agreement" — separate from Privacy (eye), Acceptable Use
  // (shield), and Cookies (cookie).
  | 'terms'
  // Push 5-post-smoke-33 (Sub-agent V, 2026-07-15) — Analytics-lane
  // kinds. Each of the four analytics children gets its OWN kind so
  // AdminSubNav can render UNIQUE sub-child icons (Ean directive: sub
  // child icons should be unique + logical, not the parent's icon
  // repeated four times). Also swaps Analytics parent off 'activity'
  // (which stays reserved for Insights via IconSparkle) to a dedicated
  // 'analytics' kind carrying the bar-chart glyph.
  | 'analytics'
  | 'dashboard'
  | 'pulse'
  | 'insights'
  // Push 5-post-smoke-33 (Sub-agent V, 2026-07-15) — Permissions-lane
  // child kinds. Distinct so the two Permissions children (Sets +
  // Assignments) render different glyphs from each other AND from the
  // parent's IconLock. Sets = shield (protection/policy bundle);
  // Assignments = link (the junction row binding two records).
  | 'permission-set-shield'
  | 'assignment-link';

export type ObjectColor = 'teal' | 'obsidian';

interface ObjectMeta {
  render: (size: number) => ReactElement;
  bg: ObjectColor;
  label: string;
}

const REGISTRY: Record<ObjectKind, ObjectMeta> = {
  // Pillars (teal). D-167: Models → Connectors → Prompts
  models:            { render: (s) => <IconBolt size={s} />,       bg: 'teal',     label: 'Models' },
  connectors:        { render: (s) => <IconPlug size={s} />,       bg: 'teal',     label: 'Connectors' },
  prompts:           { render: (s) => <IconWave size={s} />,       bg: 'teal',     label: 'Prompts' },
  // User-mode surfaces (teal)
  home:              { render: (s) => <IconHome size={s} />,       bg: 'teal',     label: 'Home' },
  profile:           { render: (s) => <IconUserCircle size={s} />, bg: 'teal',     label: 'Profile' },
  catalog:           { render: (s) => <IconGrid size={s} />,       bg: 'teal',     label: 'Market' },
  chat:              { render: (s) => <IconChat size={s} />,       bg: 'teal',     label: 'Chat' },
  // Admin parent grouper (teal) — used on the Capabilities ▾ sidebar parent
  // since its children are all teal pillars; a teal layers-icon ties them
  // together visually without claiming a specific pillar's mark.
  capabilities:      { render: (s) => <IconLayers size={s} />,     bg: 'teal',     label: 'Capabilities' },
  // Governance surfaces (obsidian)
  people:            { render: (s) => <IconPeople size={s} />,     bg: 'obsidian', label: 'People' },
  'permission-sets': { render: (s) => <IconLock size={s} />,       bg: 'obsidian', label: 'Permission sets' },
  activity:          { render: (s) => <IconSparkle size={s} />,    bg: 'obsidian', label: 'Activity' },
  // Push #78 round 21 (item G) — Incidents nav surface. Warning-triangle
  // glyph on obsidian tile so it reads as governance/look-back, not action.
  incidents:         { render: (s) => <IconAlert size={s} />,      bg: 'obsidian', label: 'Incidents' },
  inbox:             { render: (s) => <IconInbox size={s} />,      bg: 'obsidian', label: 'Inbox' },
  billing:           { render: (s) => <IconReceipt size={s} />,    bg: 'obsidian', label: 'Billing' },
  settings:          { render: (s) => <IconGear size={s} />,       bg: 'obsidian', label: 'Settings' },
  // Push 5-post-smoke-32 (Sub-agent U, 2026-07-15). Briefcase mark
  // dedicated to the Workspace admin sidebar surface. Reads as "your
  // team's workspace container" rather than "settings/preferences",
  // which is the truer concept for a hub that carries General +
  // Usage + Billing + Notifications tabs.
  workspace:         { render: (s) => <IconBriefcase size={s} />,  bg: 'obsidian', label: 'Workspace' },
  // Phase 2 Block 2B (2026-06-30) — operator + junction marks.
  // Obsidian tile (governance lane) for both; operator = shield (platform
  // owns this data), junction = chain link (edge between two records).
  operator:          { render: (s) => <IconShield size={s} />,     bg: 'obsidian', label: 'Operator record' },
  junction:          { render: (s) => <IconLink size={s} />,       bg: 'obsidian', label: 'Junction' },
  // Phase 2 Block 2B per-table marks. Locked per execution plan § Block 2C.
  // Tone follows the two-color split: chat-adjacent + capability-instance
  // surfaces are teal (things you DO with capabilities); governance,
  // billing, and notification surfaces are obsidian (things you GOVERN).
  notification:      { render: (s) => <IconBell size={s} />,       bg: 'obsidian', label: 'Notification' },
  invitation:        { render: (s) => <IconMail size={s} />,       bg: 'obsidian', label: 'Invitation' },
  folder:            { render: (s) => <IconFolder size={s} />,     bg: 'teal',     label: 'Folder' },
  // Push 4-1c-d (2026-07-01) — Library pillar mark (Group 2 BYO
  // collection surface). Book-stack glyph distinguishes from Folder
  // (organizational unit inside Library). Teal per capability-surface
  // convention.
  library:           { render: (s) => <IconLibrary size={s} />,    bg: 'teal',     label: 'Library' },
  message:           { render: (s) => <IconMessage size={s} />,    bg: 'teal',     label: 'Message' },
  attachment:        { render: (s) => <IconPaperclip size={s} />,  bg: 'teal',     label: 'Attachment' },
  'token-grant':     { render: (s) => <IconReceipt size={s} />,    bg: 'obsidian', label: 'Token grant' },
  'token-debit':     { render: (s) => <IconCoin size={s} />,       bg: 'obsidian', label: 'Token debit' },
  'approval-request':{ render: (s) => <IconClipboard size={s} />,  bg: 'obsidian', label: 'Approval request' },
  product:           { render: (s) => <IconPackage size={s} />,    bg: 'obsidian', label: 'Product' },
  // Phase 4 pre-plumbing (2026-06-30) — documents capability.
  document:          { render: (s) => <IconLayers size={s} />,     bg: 'teal',     label: 'Document' },
  'document-chunk':  { render: (s) => <IconGrid size={s} />,       bg: 'teal',     label: 'Document chunk' },
  // Push 4-1o (2026-07-09) — legal-tab marks. Obsidian per legal palette.
  privacy:           { render: (s) => <IconEye size={s} />,        bg: 'obsidian', label: 'Privacy' },
  cookies:           { render: (s) => <IconCookie size={s} />,     bg: 'obsidian', label: 'Cookies' },
  terms:             { render: (s) => <IconFileText size={s} />,   bg: 'obsidian', label: 'Terms of Service' },
  // Push 5-post-smoke-33 (Sub-agent V, 2026-07-15) — Analytics-lane marks.
  // Parent (bar chart) + three child kinds. Sparkle stays as the vocabulary
  // for AI-generated insights (matches Anthropic / Claude.ai convention);
  // dashboard-panel vocabulary for the aggregate Overview page; pulse
  // (heartbeat monitor line) for the raw event-feed Activity child. All
  // obsidian per the governance-lane palette.
  analytics:         { render: (s) => <IconBarChart size={s} />,   bg: 'obsidian', label: 'Analytics' },
  dashboard:         { render: (s) => <IconDashboard size={s} />,  bg: 'obsidian', label: 'Overview' },
  pulse:             { render: (s) => <IconPulse size={s} />,      bg: 'obsidian', label: 'Activity' },
  insights:          { render: (s) => <IconSparkle size={s} />,    bg: 'obsidian', label: 'Insights' },
  // Push 5-post-smoke-33 (Sub-agent V, 2026-07-15) — Permissions-lane
  // child marks. Distinct from parent IconLock so the two children
  // ('Permission Sets' = shield, 'Assignments' = user-check) each carry
  // their own logical glyph. Both obsidian.
  // Push 52 (2026-07-15, Ean smoke walk) — 'assignment-link' swapped
  // from IconLink (chain link — reads as generic connection) to
  // IconUserCheck (Lucide user-check — person with a checkmark, reads
  // immediately as "assigned"). The kind slug is retained so callsites
  // don't have to update. IconLink stays available for the junction
  // ObjectIcon kind + other generic-connection uses.
  'permission-set-shield': { render: (s) => <IconShield size={s} />,   bg: 'obsidian', label: 'Permission set' },
  'assignment-link':       { render: (s) => <IconUserCheck size={s} />, bg: 'obsidian', label: 'Assignment' },
};

const SIZE_MAP = {
  xs: { tile: 20, glyph: 12, radius: 5 },
  sm: { tile: 28, glyph: 16, radius: 7 },
  md: { tile: 40, glyph: 22, radius: 10 },
  lg: { tile: 56, glyph: 30, radius: 12 },
} as const;

interface ObjectIconProps {
  kind: ObjectKind;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  /** Solid (default) = colored bg + white glyph (anchor surfaces).
   *  Soft     = tinted bg + colored glyph (EmptyState illustrations).
   *  Glyph    = bare SVG, no tile wrapper. For use INSIDE another shape —
   *             e.g. a circular icon-btn where stacking a rounded-rect tile
   *             inside a circle reads as "square in a circle" (Push #76C
   *             Ean dogfood 2026-06-15). The glyph inherits its color from
   *             the surrounding button's `color`, so the parent owns tone.
   */
  variant?: 'solid' | 'soft' | 'glyph';
  /** Push #21 override — force a specific tone regardless of the kind's
   *  registry color. Used by AdminSubNav to unify all nav-bar icons to
   *  obsidian (the nav's job is wayfinding, not meaning encoding). Inside
   *  page content, the registry's per-kind color stays the default. */
  tone?: ObjectColor;
  className?: string;
}

const COLOR_VAR: Record<ObjectColor, string> = {
  teal: 'var(--olytic-teal)',
  obsidian: 'var(--olytic-obsidian)',
};

const SOFT_TINT: Record<ObjectColor, string> = {
  // ~10% opacity hex values matching the brand teal + obsidian.
  // (Picked over rgba() so the registry stays text-readable; the tile
  //  backgrounds end up close to the dashboard card hover states.)
  teal: 'color-mix(in srgb, var(--olytic-teal) 10%, transparent)',
  obsidian: 'rgba(13, 22, 33, 0.08)',
};

export function ObjectIcon({ kind, size = 'md', variant = 'solid', tone, className }: ObjectIconProps) {
  const meta = REGISTRY[kind];
  const dim = SIZE_MAP[size];
  // Push #21: when `tone` is supplied, override the registry's per-kind
  // color. Used by AdminSubNav to render every leaf icon in obsidian for
  // wayfinding consistency. When unspecified, the registry's bg color
  // wins (page-header tiles, EmptyState illustrations, list-row icons).
  const effective = tone ?? meta.bg;
  // Push #76C — bare-glyph mode: no wrapping tile, no bg, no border. The
  // SVG path renders with currentColor so the surrounding button (e.g.
  // a circular .icon-btn) owns the color. Eliminates the "square in a
  // circle" effect when an ObjectIcon was being stacked inside a
  // pre-existing round button.
  if (variant === 'glyph') {
    return (
      <span
        className={className === undefined ? undefined : className}
        role="img"
        aria-label={meta.label}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'inherit' }}
      >
        {meta.render(dim.glyph)}
      </span>
    );
  }
  const isSoft = variant === 'soft';
  return (
    <span
      className={className === undefined ? 'tile-icon' : `tile-icon ${className}`}
      style={{
        background: isSoft ? SOFT_TINT[effective] : COLOR_VAR[effective],
        color: isSoft ? COLOR_VAR[effective] : 'var(--olytic-white)',
        width: `${dim.tile}px`,
        height: `${dim.tile}px`,
        borderRadius: `${dim.radius}px`,
      }}
      role="img"
      aria-label={meta.label}
    >
      {meta.render(dim.glyph)}
    </span>
  );
}
